import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { COUNTRIES, COUNTRY_NAME, flagEmoji } from "@/lib/countries";
import {
  ShieldCheck, ShieldBan, ShieldX, RefreshCw, Loader2, Globe2,
  Ban, CheckCircle2, XCircle, Trash2, Plus, MapPin, Wifi, KeyRound,
  Fingerprint, Lock, HardDriveUpload, AlertTriangle, Search,
} from "lucide-react";
import WorldHeatmap from "@/components/WorldHeatmap";
import {
  decodeCreationOptions, encodeRegistrationCredential, webauthnSupported,
} from "@/lib/webauthn";

export default function SecurityStatsPage() {
  const [summary, setSummary] = useState(null);
  const [myIp, setMyIp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  // Deep-link support: `/security-stats?tab=yubikey` opens directly on the
  // requested tab (used by the master admin dialog's "Manage security keys" link).
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const initialTab = params.get("tab") || "attempts";

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: s }, { data: ip }] = await Promise.all([
          api.get("/security/summary"), api.get("/security/my-ip"),
        ]);
        setSummary(s); setMyIp(ip);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load security summary");
      } finally { setLoading(false); }
    })();
  }, [refreshKey]);

  const addRule = async (ip, list, note) => {
    try {
      await api.post("/security/ip-rules", { ip, list, note });
      toast.success(`${ip} added to ${list}`);
      refresh();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add rule");
      return false;
    }
  };

  return (
    <div className="p-6 max-w-7xl" data-testid="security-stats-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-blue-100 text-[#002FA7]">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">SYSTEM</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Security Stats</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Login attempts, blocked traffic, IP whitelist/blacklist and country access control.
          </p>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm" onClick={refresh}
          disabled={loading} data-testid="security-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                   : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      <SummaryRow summary={summary} myIp={myIp} />

      <Tabs defaultValue={initialTab} className="mt-5" data-testid="security-tabs">
        <TabsList className="rounded-sm">
          <TabsTrigger value="attempts" data-testid="security-tab-attempts">Login attempts</TabsTrigger>
          <TabsTrigger value="blocked" data-testid="security-tab-blocked">Blocked traffic</TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="security-tab-heatmap">Attack origins</TabsTrigger>
          <TabsTrigger value="rules" data-testid="security-tab-rules">IP rules</TabsTrigger>
          <TabsTrigger value="countries" data-testid="security-tab-countries">Country access</TabsTrigger>
          <TabsTrigger value="yubikey" data-testid="security-tab-yubikey">YubiKey</TabsTrigger>
          <TabsTrigger value="pre-publish" data-testid="security-tab-pre-publish">Pre-publish check</TabsTrigger>
        </TabsList>
        <TabsContent value="attempts" className="mt-4">
          <AttemptsTab refreshKey={refreshKey} addRule={addRule} />
        </TabsContent>
        <TabsContent value="blocked" className="mt-4">
          <BlockedTab refreshKey={refreshKey} addRule={addRule} />
        </TabsContent>
        <TabsContent value="heatmap" className="mt-4">
          <HeatmapTab refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="rules" className="mt-4">
          <RulesTab refreshKey={refreshKey} refresh={refresh} addRule={addRule} myIp={myIp} />
        </TabsContent>
        <TabsContent value="countries" className="mt-4">
          <CountriesTab refreshKey={refreshKey} refresh={refresh} />
        </TabsContent>
        <TabsContent value="yubikey" className="mt-4">
          <YubikeyTab refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="pre-publish" className="mt-4">
          <PrePublishTab refreshKey={refreshKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryRow({ summary, myIp }) {
  const s = summary || {};
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      <Pill label="Attempts (24h)" value={s.attempts_24h ?? "—"} icon={KeyRound}
        testid="security-pill-attempts" />
      <Pill label="Failed (24h)" value={s.failed_24h ?? "—"} icon={XCircle}
        accent={(s.failed_24h ?? 0) > 0 ? "text-red-600" : ""} testid="security-pill-failed" />
      <Pill label="Successful (24h)" value={s.success_24h ?? "—"} icon={CheckCircle2}
        accent="text-emerald-700" testid="security-pill-success" />
      <Pill label="Blocked hits (7d)" value={s.blocked_hits_7d ?? "—"} icon={Ban}
        accent={(s.blocked_hits_7d ?? 0) > 0 ? "text-amber-700" : ""} testid="security-pill-blocked" />
      <Pill label="Country lock" icon={Globe2} testid="security-pill-enforcement"
        value={s.enforcement_enabled
          ? <span className="text-emerald-700">ON · {(s.allowed_countries || []).join(", ")}</span>
          : <span className="text-zinc-400">OFF</span>} />
      <Pill label="Your IP" icon={Wifi} testid="security-pill-myip"
        value={myIp ? (
          <span className="text-sm font-mono">
            {flagEmoji(myIp.geo?.country_code)} {myIp.ip}
          </span>
        ) : "—"} />
    </div>
  );
}

function Pill({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white" data-testid={testid}>
      <div className="label-overline text-[9px] flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className={`font-display font-bold text-xl mt-1 truncate ${accent || ""}`}>{value}</div>
    </div>
  );
}

// ── Login attempts ─────────────────────────────────────────────────────
const STATUS_STYLES = {
  success: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "2fa_sent": "bg-blue-100 text-blue-800 border-blue-200",
  failed_password: "bg-red-100 text-red-700 border-red-200",
  "2fa_failed": "bg-red-100 text-red-700 border-red-200",
  "2fa_expired": "bg-amber-100 text-amber-800 border-amber-200",
};
const STATUS_LABELS = {
  success: "SUCCESS", "2fa_sent": "2FA SENT", failed_password: "BAD PASSWORD",
  "2fa_failed": "BAD 2FA CODE", "2fa_expired": "2FA EXPIRED",
};

function AttemptsTab({ refreshKey, addRule }) {
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(30);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: "300" });
      if (status) params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/security/login-attempts?${params}`);
      setRows(data.attempts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load attempts");
    } finally { setLoading(false); }
  }, [days, status, q]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="space-y-3">
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-3 flex flex-wrap gap-3 items-end">
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
            Days
            <Input type="number" min="1" max="365" value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10) || 30)}
              className="mt-1 h-9 w-20 text-sm font-mono" data-testid="attempts-days" />
          </label>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="mt-1 h-9 block border border-zinc-200 rounded-sm px-2 text-sm bg-white"
              data-testid="attempts-status-filter">
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="failed_password">Bad password</option>
              <option value="2fa_sent">2FA sent</option>
              <option value="2fa_failed">Bad 2FA code</option>
            </select>
          </label>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex-1 min-w-[200px]">
            Search email / IP / country / city
            <Input type="search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. admin@… or 8.8.8.8 or Germany"
              className="mt-1 h-9 text-sm" data-testid="attempts-search" />
          </label>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-0 overflow-x-auto">
          <div className="grid grid-cols-[150px_1.2fr_120px_140px_1.2fr_1fr_150px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 min-w-[1050px]">
            <span>When</span><span>Account</span><span>Status</span><span>IP</span>
            <span>Location</span><span>Device</span><span className="text-right">Actions</span>
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm italic text-zinc-500" data-testid="attempts-empty">
              No login attempts recorded in this window yet.
            </div>
          ) : rows.map((r) => (
            <div key={r.attempt_id}
              className="grid grid-cols-[150px_1.2fr_120px_140px_1.2fr_1fr_150px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50 min-w-[1050px]"
              data-testid={`attempt-row-${r.attempt_id}`}>
              <span className="text-xs font-mono text-zinc-600">{fmtDT(r.created_at)}</span>
              <span className="min-w-0">
                <span className="block truncate font-semibold">{r.email || "—"}</span>
                <span className="text-[10px] text-zinc-400 uppercase">{r.role || "unknown"} · {r.method}</span>
              </span>
              <span>
                <Badge variant="outline"
                  className={`rounded-sm text-[9px] ${STATUS_STYLES[r.status] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>
                  {STATUS_LABELS[r.status] || r.status?.toUpperCase()}
                </Badge>
              </span>
              <span className="text-xs font-mono">{r.ip}</span>
              <GeoCell geo={r.geo} />
              <span className="text-xs text-zinc-600 truncate" title={r.user_agent}>{parseUA(r.user_agent)}</span>
              <IpActions ip={r.ip} addRule={addRule} testid={r.attempt_id} />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function GeoCell({ geo }) {
  if (!geo) return <span className="text-xs text-zinc-400 italic">internal / unknown</span>;
  return (
    <span className="min-w-0">
      <span className="block truncate text-xs">
        {flagEmoji(geo.country_code)} {geo.city ? `${geo.city}, ` : ""}{geo.country || "Unknown"}
        {geo.proxy && <Badge variant="outline" className="ml-1.5 rounded-sm text-[8px] bg-red-50 text-red-700 border-red-200">PROXY/VPN</Badge>}
      </span>
      <span className="block text-[10px] text-zinc-400 truncate flex items-center gap-1">
        <MapPin className="h-2.5 w-2.5 inline" /> {geo.isp || geo.org || "—"}
      </span>
    </span>
  );
}

function IpActions({ ip, addRule, testid }) {
  const [busy, setBusy] = useState(false);
  const act = async (list) => {
    setBusy(true);
    await addRule(ip, list, `Added from Security Stats`);
    setBusy(false);
  };
  return (
    <span className="flex justify-end gap-1.5">
      <Button size="sm" variant="outline" disabled={busy}
        className="rounded-sm h-7 px-2 text-[10px] text-red-700 border-red-200 hover:bg-red-50"
        onClick={() => act("blacklist")} data-testid={`block-ip-${testid}`}>
        <Ban className="h-3 w-3 mr-1" /> Block
      </Button>
      <Button size="sm" variant="outline" disabled={busy}
        className="rounded-sm h-7 px-2 text-[10px] text-emerald-700 border-emerald-200 hover:bg-emerald-50"
        onClick={() => act("whitelist")} data-testid={`trust-ip-${testid}`}>
        <CheckCircle2 className="h-3 w-3 mr-1" /> Trust
      </Button>
    </span>
  );
}

// ── Blocked traffic ────────────────────────────────────────────────────
function BlockedTab({ refreshKey, addRule }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/security/blocked-events?days=30&limit=300");
        setRows(data.events || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load blocked events");
      } finally { setLoading(false); }
    })();
  }, [refreshKey]);

  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-0 overflow-x-auto">
        <div className="grid grid-cols-[150px_140px_140px_1.2fr_70px_1fr_120px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 min-w-[980px]">
          <span>Last hit</span><span>IP</span><span>Reason</span><span>Location</span>
          <span>Hits</span><span>Last path</span><span className="text-right">Actions</span>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm italic text-zinc-500" data-testid="blocked-empty">
            No blocked traffic in the last 30 days.
          </div>
        ) : rows.map((r, i) => (
          <div key={`${r.ip}-${r.bucket}-${r.reason}`}
            className="grid grid-cols-[150px_140px_140px_1.2fr_70px_1fr_120px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50 min-w-[980px]"
            data-testid={`blocked-row-${i}`}>
            <span className="text-xs font-mono text-zinc-600">{fmtDT(r.last_at)}</span>
            <span className="text-xs font-mono">{r.ip}</span>
            <span>
              <Badge variant="outline"
                className={`rounded-sm text-[9px] ${r.reason === "ip_blacklisted"
                  ? "bg-red-100 text-red-700 border-red-200"
                  : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                {r.reason === "ip_blacklisted" ? "BLACKLISTED IP" : "COUNTRY BLOCKED"}
              </Badge>
            </span>
            <GeoCell geo={r.geo} />
            <span className="text-xs font-mono font-bold">{r.count}</span>
            <span className="text-[11px] font-mono text-zinc-500 truncate" title={r.last_path}>{r.last_path}</span>
            <span className="flex justify-end gap-1">
              <Button size="sm" variant="outline"
                className="rounded-sm h-7 px-2 text-[10px] text-[#002FA7] border-zinc-200 hover:bg-zinc-50"
                asChild
                data-testid={`blocked-enrich-${i}`}>
                <a href={`/ip-api?ip=${encodeURIComponent(r.ip)}`}
                   target="_blank" rel="noreferrer" title="Look up on IP API">
                  <Search className="h-3 w-3 mr-1" /> Enrich
                </a>
              </Button>
              <Button size="sm" variant="outline"
                className="rounded-sm h-7 px-2 text-[10px] text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={() => addRule(r.ip, "whitelist", "Approved from Blocked traffic tab")}
                data-testid={`blocked-trust-${i}`}>
                <CheckCircle2 className="h-3 w-3 mr-1" /> Whitelist
              </Button>
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ── IP rules ───────────────────────────────────────────────────────────
function RulesTab({ refreshKey, refresh, addRule, myIp }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ip, setIp] = useState("");
  const [list, setList] = useState("blacklist");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/security/ip-rules");
        setRules(data.rules || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load IP rules");
      } finally { setLoading(false); }
    })();
  }, [refreshKey]);

  const submit = async (e) => {
    e.preventDefault();
    if (!ip.trim()) return;
    setBusy(true);
    const ok = await addRule(ip.trim(), list, note);
    setBusy(false);
    if (ok) { setIp(""); setNote(""); }
  };

  const remove = async (rule) => {
    try {
      await api.delete(`/security/ip-rules/${rule.rule_id}`);
      toast.success(`${rule.ip} removed from ${rule.list}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete rule");
    }
  };

  const white = rules.filter((r) => r.list === "whitelist");
  const black = rules.filter((r) => r.list === "blacklist");

  return (
    <div className="space-y-4">
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-4">
          <form onSubmit={submit} className="flex flex-wrap gap-3 items-end" data-testid="ip-rule-form">
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
              IP address or CIDR range
              <Input value={ip} onChange={(e) => setIp(e.target.value)}
                placeholder="e.g. 203.5.10.20 or 203.5.10.0/24"
                className="mt-1 h-9 w-56 text-sm font-mono" data-testid="ip-rule-ip-input" />
            </label>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
              List
              <select value={list} onChange={(e) => setList(e.target.value)}
                className="mt-1 h-9 block border border-zinc-200 rounded-sm px-2 text-sm bg-white"
                data-testid="ip-rule-list-select">
                <option value="blacklist">Blacklist (block)</option>
                <option value="whitelist">Whitelist (always allow)</option>
              </select>
            </label>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex-1 min-w-[180px]">
              Note
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Why is this IP listed?" className="mt-1 h-9 text-sm"
                data-testid="ip-rule-note-input" />
            </label>
            <Button type="submit" disabled={busy || !ip.trim()}
              className="h-9 rounded-sm bg-[#002FA7] hover:bg-[#00227A]" data-testid="ip-rule-add-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add rule</>}
            </Button>
            {myIp?.ip && (
              <Button type="button" variant="outline" className="h-9 rounded-sm text-xs"
                onClick={() => setIp(myIp.ip)} data-testid="ip-rule-use-my-ip">
                Use my IP ({myIp.ip})
              </Button>
            )}
          </form>
          <p className="text-[11px] text-zinc-500 mt-2">
            Whitelist always wins — a whitelisted IP is allowed even when its country is blocked
            or it also appears on the blacklist.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RuleList title="Whitelist — always allowed" icon={ShieldCheck} rows={white}
          loading={loading} tone="emerald" onDelete={remove} testid="whitelist" />
        <RuleList title="Blacklist — always blocked" icon={ShieldBan} rows={black}
          loading={loading} tone="red" onDelete={remove} testid="blacklist" />
      </div>
    </div>
  );
}

function RuleList({ title, icon: Icon, rows, loading, tone, onDelete, testid }) {
  return (
    <Card className="rounded-sm border-zinc-200" data-testid={`${testid}-card`}>
      <CardContent className="p-0">
        <div className={`px-3 py-2 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold flex items-center gap-1.5 ${tone === "red" ? "bg-red-50 text-red-800" : "bg-emerald-50 text-emerald-800"}`}>
          <Icon className="h-3.5 w-3.5" /> {title} · {rows.length}
        </div>
        {loading ? (
          <div className="p-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-xs italic text-zinc-500" data-testid={`${testid}-empty`}>
            No entries yet.
          </div>
        ) : rows.map((r) => (
          <div key={r.rule_id}
            className="flex items-center gap-3 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm hover:bg-zinc-50"
            data-testid={`rule-row-${r.rule_id}`}>
            <span className="font-mono text-xs w-36 shrink-0">{r.ip}</span>
            <span className="text-xs w-32 shrink-0 truncate">
              {r.geo ? <>{flagEmoji(r.geo.country_code)} {r.geo.country}</> : <span className="text-zinc-400">—</span>}
            </span>
            <span className="flex-1 min-w-0 text-xs text-zinc-500 truncate" title={r.note}>{r.note || "—"}</span>
            <span className="text-[10px] text-zinc-400 shrink-0 hidden xl:block">{fmtDT(r.created_at)}</span>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600 shrink-0"
              onClick={() => onDelete(r)} data-testid={`rule-delete-${r.rule_id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ── Country access ─────────────────────────────────────────────────────
function CountriesTab({ refreshKey, refresh }) {
  const [settings, setSettings] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/security/settings");
        setSettings(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load settings");
      }
    })();
  }, [refreshKey]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/security/settings", patch);
      setSettings(data);
      if (data.auto_whitelisted_ip) {
        toast.success(`Enforcement enabled — your IP ${data.auto_whitelisted_ip} was auto-whitelisted so you can't lock yourself out.`);
      } else {
        toast.success("Security settings saved");
      }
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally { setSaving(false); }
  };

  if (!settings) return <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  const allowed = settings.allowed_countries || [];
  const matches = search.trim()
    ? COUNTRIES.filter(([code, name]) =>
        !allowed.includes(code) &&
        (name.toLowerCase().includes(search.toLowerCase()) ||
         code.toLowerCase() === search.toLowerCase())).slice(0, 8)
    : [];

  return (
    <div className="space-y-4 max-w-3xl">
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="flex-1">
            <div className="font-semibold text-sm flex items-center gap-2">
              <Globe2 className="h-4 w-4 text-[#002FA7]" /> Country allow-list enforcement
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              When ON, every visitor outside the approved countries below is blocked from the
              entire platform (login pages, client portals and public pay links). Whitelisted
              IPs always get through. Your current IP is auto-whitelisted when you switch this on.
            </p>
          </div>
          <Switch checked={!!settings.enforcement_enabled} disabled={saving}
            onCheckedChange={(v) => save({ enforcement_enabled: v })}
            data-testid="country-enforcement-toggle" />
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-4 space-y-3">
          <div className="label-overline">Approved countries ({allowed.length})</div>
          <div className="flex flex-wrap gap-2" data-testid="allowed-countries-list">
            {allowed.length === 0 && <span className="text-xs italic text-zinc-400">None yet — add at least one before enabling enforcement.</span>}
            {allowed.map((code) => (
              <Badge key={code} variant="outline"
                className="rounded-sm px-2 py-1 text-xs bg-emerald-50 text-emerald-800 border-emerald-200 flex items-center gap-1.5"
                data-testid={`allowed-country-${code}`}>
                {flagEmoji(code)} {COUNTRY_NAME[code] || code}
                <button type="button" className="ml-0.5 hover:text-red-600" disabled={saving}
                  onClick={() => save({ allowed_countries: allowed.filter((c) => c !== code) })}
                  data-testid={`remove-country-${code}`}>
                  <XCircle className="h-3.5 w-3.5" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="relative">
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a country to approve (e.g. Australia, NZ, GB…)"
              className="h-10 rounded-sm text-sm" data-testid="country-search-input" />
            {matches.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-zinc-200 rounded-sm shadow-lg max-h-64 overflow-y-auto">
                {matches.map(([code, name]) => (
                  <button key={code} type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center gap-2"
                    onClick={() => { save({ allowed_countries: [...allowed, code] }); setSearch(""); }}
                    data-testid={`add-country-${code}`}>
                    {flagEmoji(code)} {name} <span className="text-zinc-400 text-xs">({code})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-4">
          <div className="label-overline mb-1">GeoIP provider</div>
          <p className="text-xs text-zinc-500">
            Currently using <strong>ip-api.com</strong> (free tier, results cached for 14 days).
            MaxMind GeoIP2 support is scaffolded — add your account ID and licence key here once
            you have a subscription and we can switch providers.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Attack origins heat-map ────────────────────────────────────────────
function HeatmapTab({ refreshKey }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/security/attack-map?days=${days}`);
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load attack map");
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const rows = (data?.countries || []).slice(0, 12);

  return (
    <div className="space-y-3">
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-overline">Attack origins · last {days} days</div>
              <p className="text-xs text-zinc-500 mt-0.5">
                Countries where failed login attempts or blocked traffic came from.
                Darker red = more events. Hover a country for the exact counts.
              </p>
            </div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
              Days
              <Input type="number" min="1" max="365" value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10) || 30)}
                className="mt-1 h-9 w-20 text-sm font-mono" data-testid="heatmap-days-input" />
            </label>
          </div>
          <WorldHeatmap countries={data?.countries || []} loading={loading} />
        </CardContent>
      </Card>
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-0 overflow-x-auto">
          <div className="grid grid-cols-[60px_1.4fr_100px_110px_110px_1fr_140px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 min-w-[880px]">
            <span>Flag</span><span>Country</span><span className="text-right">Total</span>
            <span className="text-right">Failed logins</span><span className="text-right">Blocked hits</span>
            <span>Sample cities</span><span className="text-right">Last event</span>
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm italic text-zinc-500" data-testid="heatmap-empty">
              No geolocatable attack traffic in the last {days} days.
            </div>
          ) : rows.map((r) => (
            <div key={r.code}
              className="grid grid-cols-[60px_1.4fr_100px_110px_110px_1fr_140px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50 min-w-[880px]"
              data-testid={`heatmap-row-${r.code}`}>
              <span className="text-lg">{flagEmoji(r.code)}</span>
              <span className="min-w-0">
                <span className="block truncate font-semibold">{r.country}</span>
                <span className="text-[10px] text-zinc-400 uppercase">{r.code} · {r.distinct_ips} IPs</span>
              </span>
              <span className="font-mono font-bold text-right">{r.total}</span>
              <span className="font-mono text-right text-red-700">{r.attempts}</span>
              <span className="font-mono text-right text-amber-700">{r.blocked_hits}</span>
              <span className="text-xs text-zinc-600 truncate">{(r.cities || []).join(", ") || "—"}</span>
              <span className="text-[11px] font-mono text-zinc-500 text-right">{fmtDT(r.last_at)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ── YubiKey (WebAuthn) management ──────────────────────────────────────
function YubikeyTab({ refreshKey }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [showFallbackForm, setShowFallbackForm] = useState(false);
  const [fallbackPwd, setFallbackPwd] = useState("");
  const [fallbackConfirm, setFallbackConfirm] = useState("");
  const [busyFallback, setBusyFallback] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/security/yubikey/status");
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load YubiKey status");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const handleRegister = async () => {
    if (!webauthnSupported()) {
      toast.error("This browser doesn't support WebAuthn (needs Chrome, Edge, Safari, Firefox on HTTPS).");
      return;
    }
    setRegistering(true);
    try {
      const { data: begin } = await api.post("/security/yubikey/register/begin", {});
      const opts = decodeCreationOptions(begin.publicKey);
      const credential = await navigator.credentials.create({ publicKey: opts });
      if (!credential) { toast.error("Registration cancelled"); return; }
      const payload = encodeRegistrationCredential(credential);
      await api.post("/security/yubikey/register/finish", {
        challenge_id: begin.challenge_id,
        credential: payload,
        name: keyName || null,
      });
      toast.success("YubiKey registered — you'll be prompted for it on your next login.");
      setKeyName("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to register YubiKey");
    } finally { setRegistering(false); }
  };

  const handleDelete = async (cred_id) => {
    if (!window.confirm("Remove this security key? You won't be able to sign in with it anymore.")) return;
    try {
      await api.delete(`/security/yubikey/keys/${cred_id}`);
      toast.success("Security key removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete key");
    }
  };

  const handleSetFallback = async (e) => {
    e.preventDefault();
    if (fallbackPwd !== fallbackConfirm) {
      toast.error("Passwords don't match"); return;
    }
    if (fallbackPwd.length < 12) {
      toast.error("Master fallback must be at least 12 characters"); return;
    }
    setBusyFallback(true);
    try {
      await api.put("/security/yubikey/fallback-password", { password: fallbackPwd });
      toast.success("Master fallback password saved (bcrypt-hashed)");
      setFallbackPwd(""); setFallbackConfirm(""); setShowFallbackForm(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save fallback");
    } finally { setBusyFallback(false); }
  };

  if (loading || !status) return (
    <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
  );

  if (!status.is_master_admin) return (
    <Card className="rounded-sm border-amber-200 bg-amber-50">
      <CardContent className="p-5 text-sm text-amber-900 flex items-start gap-3">
        <Lock className="h-5 w-5 mt-0.5" />
        <div>
          <div className="font-semibold">Reserved for the master admin</div>
          <p className="text-xs mt-1">
            YubiKey (FIDO2) hardware second-factor is enabled only for the master admin
            account (<code className="font-mono">{status.master_admin_email}</code>).
            Every other user relies on the mandatory email 2FA layer.
          </p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4 max-w-3xl">
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-sm bg-blue-100 grid place-items-center text-[#002FA7]">
              <Fingerprint className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg">YubiKey (FIDO2 / WebAuthn)</div>
              <p className="text-sm text-zinc-500 mt-0.5">
                Hardware second-factor layered <strong>after</strong> the email 2FA code.
                Register up to {status.max_keys} keys (keep a backup somewhere safe).
                On next sign-in, touch a registered key to complete login.
              </p>
              <p className="text-[11px] text-zinc-400 mt-1">
                {status.credentials.length} / {status.max_keys} registered
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 items-end">
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex-1 min-w-[220px]">
              Key label (optional)
              <Input value={keyName} onChange={(e) => setKeyName(e.target.value)}
                placeholder="e.g. Yubikey — Office desk"
                className="mt-1 h-10 rounded-sm" data-testid="yubikey-name-input" />
            </label>
            <Button onClick={handleRegister} disabled={registering || status.credentials.length >= status.max_keys}
              className="h-10 rounded-sm bg-[#002FA7] hover:bg-[#00227A]"
              data-testid="yubikey-register-button">
              {registering ? <Loader2 className="h-4 w-4 animate-spin" /> :
                <><Plus className="h-4 w-4 mr-1.5" /> Register a new YubiKey</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-0">
          <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 flex items-center gap-2">
            <KeyRound className="h-3.5 w-3.5" /> Registered keys
          </div>
          {status.credentials.length === 0 ? (
            <div className="p-6 text-center text-sm italic text-zinc-500" data-testid="yubikey-empty">
              No YubiKeys registered yet. Sign-ins currently rely on the email 2FA only.
              Add a key to enforce hardware verification for your account.
            </div>
          ) : status.credentials.map((k) => (
            <div key={k.cred_id}
              className="flex items-center gap-3 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm hover:bg-zinc-50"
              data-testid={`yubikey-row-${k.cred_id}`}>
              <Fingerprint className="h-4 w-4 text-[#002FA7] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{k.name || "Unnamed key"}</div>
                <div className="text-[10px] text-zinc-400 font-mono truncate">
                  {(k.transports || []).join(" · ") || "unknown transports"}
                  {" · "}sign #{k.sign_count}
                  {" · "}added {fmtDT(k.created_at)}
                  {k.last_used_at && (" · last used " + fmtDT(k.last_used_at))}
                </div>
              </div>
              <Button size="sm" variant="ghost"
                className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600"
                onClick={() => handleDelete(k.cred_id)}
                data-testid={`yubikey-delete-${k.cred_id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <ShieldX className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">Master fallback password</div>
              <p className="text-sm text-zinc-500 mt-0.5">
                A last-ditch secret you can enter at login if all YubiKeys are lost.
                Stored bcrypt-hashed. Only reachable at the YubiKey step of the master
                admin login — email 2FA still applies.
              </p>
              <p className="text-[11px] mt-1">
                {status.fallback_configured
                  ? <span className="text-emerald-700 font-semibold">
                      Configured{status.fallback_updated_at ? ` · ${fmtDT(status.fallback_updated_at)}` : ""}
                      {status.fallback_updated_by ? ` · by ${status.fallback_updated_by}` : ""}
                    </span>
                  : <span className="text-amber-700 font-semibold">Not yet configured — set one before you register your first YubiKey.</span>}
              </p>
            </div>
            <Button variant="outline" className="rounded-sm h-9"
              onClick={() => setShowFallbackForm((v) => !v)}
              data-testid="yubikey-fallback-toggle-button">
              {status.fallback_configured ? "Rotate password" : "Set password"}
            </Button>
          </div>
          {showFallbackForm && (
            <form onSubmit={handleSetFallback} className="mt-4 grid gap-3" data-testid="yubikey-fallback-form">
              <div className="grid gap-1.5">
                <label className="label-overline text-xs">New master fallback password (≥12 chars)</label>
                <Input type="password" value={fallbackPwd} onChange={(e) => setFallbackPwd(e.target.value)}
                  className="h-10 rounded-sm font-mono" required
                  data-testid="yubikey-fallback-new-input" />
              </div>
              <div className="grid gap-1.5">
                <label className="label-overline text-xs">Confirm</label>
                <Input type="password" value={fallbackConfirm} onChange={(e) => setFallbackConfirm(e.target.value)}
                  className="h-10 rounded-sm font-mono" required
                  data-testid="yubikey-fallback-confirm-input" />
              </div>
              <Button type="submit" disabled={busyFallback}
                className="h-10 rounded-sm bg-[#002FA7] hover:bg-[#00227A] w-fit"
                data-testid="yubikey-fallback-save-button">
                {busyFallback ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save fallback"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────
function PrePublishTab({ refreshKey }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/security/file-sync/status");
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load file-sync status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const runSync = async () => {
    if (!window.confirm(
      "Push every un-mirrored local file to persistent object storage AND "
      + "restore any indexed file missing from local disk?\n\n"
      + "Safe to run any time. Usually takes a few seconds — larger "
      + "libraries can take a minute.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/security/file-sync/run");
      const s = data.stats || {};
      toast.success(
        `Sync complete — uploaded ${s.uploaded || 0}, restored `
        + `${s.restored || 0}, skipped ${s.skipped || 0}.`,
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const s = status || {};
  const safe = s.safe_to_publish;
  const unsynced = s.unsynced_count || 0;
  const missing = s.missing_local_count || 0;
  const indexed = s.index_count || 0;

  return (
    <div className="space-y-4" data-testid="pre-publish-tab">
      <Card className="rounded-sm">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 grid place-items-center rounded-sm ${
              safe ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
            }`}>
              {safe ? <CheckCircle2 className="h-6 w-6" />
                    : <AlertTriangle className="h-6 w-6" />}
            </div>
            <div className="flex-1">
              <div className="label-overline">DEPLOYMENT SAFETY</div>
              <h3 className="text-lg font-display font-extrabold">
                {loading ? "Checking…"
                  : safe ? "Safe to publish — every local file is mirrored"
                         : `${unsynced} local file${unsynced === 1 ? "" : "s"} not yet mirrored`}
              </h3>
              <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
                Every redeploy wipes the container&apos;s local disk. Files not yet
                mirrored to persistent object storage would be lost. This
                check pushes any unsynced files to storage BEFORE you click
                Republish so nothing is lost.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button size="sm" variant="outline" onClick={load}
                disabled={loading} data-testid="pre-publish-refresh">
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                         : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                Re-check
              </Button>
              <Button size="sm" onClick={runSync}
                disabled={syncing || loading} data-testid="pre-publish-sync-now"
                className="bg-[#002FA7] hover:bg-[#001F80] text-white">
                {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                         : <HardDriveUpload className="h-3.5 w-3.5 mr-1.5" />}
                Sync now
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
            <MetricPill
              label="Files in persistent storage"
              value={indexed.toLocaleString()}
              tone="neutral" testid="pre-publish-metric-indexed"
            />
            <MetricPill
              label="Local files not yet mirrored"
              value={unsynced.toLocaleString()}
              tone={unsynced > 0 ? "warn" : "ok"}
              testid="pre-publish-metric-unsynced"
            />
            <MetricPill
              label="Indexed files missing locally"
              value={missing.toLocaleString()}
              tone={missing > 0 ? "info" : "ok"}
              hint="Restored automatically on 'Sync now' or next redeploy"
              testid="pre-publish-metric-missing"
            />
          </div>

          {s.error ? (
            <div className="mt-4 p-3 rounded-sm bg-red-50 text-red-700 text-xs">
              {s.error}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {(s.unsynced || []).length > 0 ? (
        <Card className="rounded-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-bold text-sm">
                Un-mirrored local files ({s.unsynced.length} shown
                {unsynced > s.unsynced.length ? ` of ${unsynced}` : ""})
              </h4>
              <Badge variant="outline" className="rounded-sm">Would be lost on redeploy</Badge>
            </div>
            <div className="max-h-96 overflow-y-auto border border-zinc-200 rounded-sm">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2">Path</th>
                    <th className="px-3 py-2 w-24">Size</th>
                    <th className="px-3 py-2 w-32">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {s.unsynced.map((f) => (
                    <tr key={f.subpath} className="border-t border-zinc-100"
                        data-testid="pre-publish-unsynced-row">
                      <td className="px-3 py-1.5 font-mono truncate max-w-md" title={f.subpath}>
                        {f.subpath}
                      </td>
                      <td className="px-3 py-1.5 text-zinc-600">{fmtBytes(f.size)}</td>
                      <td className="px-3 py-1.5">
                        <Badge variant="outline"
                          className={`rounded-sm text-[10px] ${
                            f.reason === "never_synced" ? "border-amber-300 text-amber-700"
                                                        : "border-blue-300 text-blue-700"
                          }`}>
                          {f.reason === "never_synced" ? "New" : "Modified"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {(s.missing_local || []).length > 0 ? (
        <Card className="rounded-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-bold text-sm">
                Indexed files missing from local disk ({s.missing_local.length} shown
                {missing > s.missing_local.length ? ` of ${missing}` : ""})
              </h4>
              <Badge variant="outline" className="rounded-sm">
                Auto-restored on demand
              </Badge>
            </div>
            <p className="text-xs text-zinc-500 mb-2">
              These files are safely in object storage — they&apos;ll be pulled
              back onto local disk the next time they&apos;re requested (or when
              you click Sync now).
            </p>
            <div className="max-h-72 overflow-y-auto border border-zinc-200 rounded-sm">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2">Path</th>
                    <th className="px-3 py-2 w-24">Size</th>
                    <th className="px-3 py-2 w-40">Last synced</th>
                  </tr>
                </thead>
                <tbody>
                  {s.missing_local.map((f) => (
                    <tr key={f.subpath} className="border-t border-zinc-100">
                      <td className="px-3 py-1.5 font-mono truncate max-w-md" title={f.subpath}>
                        {f.subpath}
                      </td>
                      <td className="px-3 py-1.5 text-zinc-600">{fmtBytes(f.size)}</td>
                      <td className="px-3 py-1.5 text-zinc-600">{fmtDT(f.synced_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function MetricPill({ label, value, tone, hint, testid }) {
  const toneCls = {
    ok:      "border-emerald-200 bg-emerald-50 text-emerald-800",
    warn:    "border-amber-300 bg-amber-50 text-amber-800",
    info:    "border-blue-200 bg-blue-50 text-blue-800",
    neutral: "border-zinc-200 bg-zinc-50 text-zinc-800",
  }[tone || "neutral"];
  return (
    <div className={`rounded-sm border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-display font-extrabold mt-1">{value}</div>
      {hint ? <div className="text-[10px] opacity-70 mt-1">{hint}</div> : null}
    </div>
  );
}

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 19);
  return d.toLocaleString(undefined, {
    month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function parseUA(ua) {
  if (!ua) return "—";
  const browser = /edg\//i.test(ua) ? "Edge" : /chrome\//i.test(ua) ? "Chrome"
    : /firefox\//i.test(ua) ? "Firefox" : /safari\//i.test(ua) ? "Safari"
    : /curl\//i.test(ua) ? "curl" : /python/i.test(ua) ? "Script" : "Other";
  const os = /windows/i.test(ua) ? "Windows" : /mac os|macintosh/i.test(ua) ? "macOS"
    : /iphone|ipad|ios/i.test(ua) ? "iOS" : /android/i.test(ua) ? "Android"
    : /linux/i.test(ua) ? "Linux" : "";
  return os ? `${browser} · ${os}` : browser;
}
